import { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Redirect, useHistory, useParams } from "react-router-dom";
import { escape } from "lodash";
import type { BusinessBillingInfo, BusinessBillingInfoUpdate } from "lens-platform-sdk";
import { useRecurly, Elements } from "@recurly/react-recurly";
import { LoadingIndicator } from "@k8slens/lds";

import { useBusinessBillingInformation } from "src/hooks/useBusinessBillingInfo";

import PublicLayout from "src/components/PublicLayout/PublicLayout";
import LinkButton from "src/components/Button/TrackedRouterButton";
import Button from "src/components/Button/TrackedButton";

import type { RecurlyData, CCFormErrors } from "./types";
import {
  billingDetailsFormDefaults,
  mapBillingInfoToForm,
  createBillingInfoQueryParams,
  ccFormFieldsKeys,
} from "./utils";
import { useBillingForm } from "./hooks/useBillingForm";
import { useCanUpdateBillingInfo } from "src/hooks/useCanUpdateBillingInfo";

import { CreditCardForm } from "./components/CreditCardForm";
import { BillingForm } from "./components/BillingForm";

import styles from "./page.module.css";

const showInvoiceMethod = (billingInfo: BusinessBillingInfo | null | undefined) => {
  if (billingInfo?.type === "invoice") {
    // If billing info is already set to invoice, we can see it
    return true;
  }

  return billingInfo?.invoiceMethodAvailable;
};

export const updateSuccessMessage = "Billing information has been updated successfully";

const UpdateBusinessBillingDetails = () => {
  const history = useHistory();
  const recurly = useRecurly();
  const formRef = useRef<HTMLFormElement>(null);

  const { businessId } = useParams<{
    businessId: string;
  }>();
  const {
    canUpdateBillingInfo,
    loading: loadingCanUpdateBillingInfo,
    error: errorWithCanUpdateBillingInfo,
  } = useCanUpdateBillingInfo(businessId);

  const {
    billingInfo,
    loading: loadingBillingInfo,
    update,
    error: errorLoading,
    updateSuccess,
    updating,
    errorUpdating,
  } = useBusinessBillingInformation(businessId);

  const {
    formValues,
    setFormValues,
    isSubmitting,
    setIsSubmitting,
    formErrors,
    setFormErrors,
    updateField,
    submitForm,
  } = useBillingForm(billingInfo, update);

  const [waitingForRedirect, setWaitingForRedirect] = useState(false);
  const loading = loadingCanUpdateBillingInfo || loadingBillingInfo || waitingForRedirect;

  const [error, setError] = useState<ReactNode | string | undefined>(undefined);
  const busy = loading || isSubmitting || updating;

  useEffect(() => {
    if (billingInfo === undefined) {
      return;
    } else if (billingInfo === null) {
      // If billing info is null, we select Credit Card as the default payment method
      setFormValues({
        ...billingDetailsFormDefaults,
        paymentMethod: { ...billingDetailsFormDefaults.paymentMethod, value: "credit_card" },
      });

      return;
    }

    const initialValues = mapBillingInfoToForm(formValues, billingInfo);

    setFormValues(initialValues);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [billingInfo]);

  const returnUrl = useMemo(() => {
    return escape(`/business/${businessId}/billing-details`);
  }, [businessId]);

  const handleThreeDsToken = useCallback(
    (actionToken: string, updateBillingInfo: BusinessBillingInfoUpdate) => {
      history.replace(
        escape(
          `/business/${businessId}/billing-details/update/three-ds/${actionToken}?${createBillingInfoQueryParams(
            updateBillingInfo,
          )}`,
        ),
      );
    },
    [businessId, history],
  );

  const handleUpdateBillingDetails = useCallback(() => {
    if (!formRef.current) {
      return;
    }
    formRef.current.requestSubmit();
  }, [formRef]);

  const isCreditCard = formValues.paymentMethod.value === "credit_card";
  const isAustralia = formValues.addressCountry.value === "AU";

  const handleSubmit = useCallback(
    (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();

      if (!formRef.current) {
        return;
      }

      if (isSubmitting) {
        return;
      }
      setError(undefined);
      setFormErrors(undefined);
      setIsSubmitting(true);

      if (isCreditCard) {
        try {
          const hasPreviousCreditCardNumber = Boolean(billingInfo?.paymentMethod);

          recurly.token(formRef.current, (err, data) => {
            const details = err?.details
              ?.map((detail) => ({
                field: detail.field,
                messages: detail.messages.filter((s) => s !== "is valid"),
              }))
              .filter((detail) => detail.messages.length > 0);

            if (err && hasPreviousCreditCardNumber) {
              const numberIsBlank = details
                ?.find((detail) => detail.field === "number")
                ?.messages?.includes("can't be blank");

              // If we already have a credit card number, we can try to send the values without a new token
              if (err.code === "validation" && numberIsBlank) {
                submitForm().then(() => setIsSubmitting(false));

                return;
              }
            }

            if (err) {
              const formErrors: CCFormErrors = {};
              let message = err.message;

              details?.forEach((detail) => {
                const field = detail.field;
                let fieldName = field;

                switch (detail.field) {
                  case "number":
                    fieldName = "Credit card number";
                    break;
                  case "month":
                    fieldName = "Expiration month";
                    break;
                  case "year":
                    fieldName = "Expiration year";
                    break;
                  case "cvv":
                    fieldName = "CVC number";
                    break;
                  case "first_name":
                    fieldName = "First name on credit card";
                    break;
                  case "last_name":
                    fieldName = "Last name on credit card";
                    break;
                }

                if (ccFormFieldsKeys.includes(detail.field)) {
                  formErrors[detail.field] = detail.messages.map((message) => `${fieldName} ${message}`);
                }
              });

              if (Object.keys(formErrors).length) {
                message = "Invalid credit card details";
              }

              setFormErrors(formErrors);
              setError(message || "Payment method update failed, please check your card details");
              setIsSubmitting(false);

              return;
            }

            if (data) {
              submitForm(data as RecurlyData).then(() => setIsSubmitting(false));
            }
          });
        } catch (error) {
          setError("An error occurred while submitting the form. Please try again.");
          setIsSubmitting(false);

          return;
        }
      } else {
        submitForm().then(() => setIsSubmitting(false));
      }

      return;
    },
    [isSubmitting, setFormErrors, setIsSubmitting, isCreditCard, billingInfo?.paymentMethod, recurly, submitForm],
  );

  useEffect(() => {
    let mounted = true;

    if (updateSuccess) {
      setWaitingForRedirect(true);
      // Wait for 2 seconds to allow the user to see the success message
      new Promise((resolve) => setTimeout(resolve, 2000)).then(() => {
        if (mounted) {
          history.push(returnUrl);
        }
      });

      return () => {
        mounted = false;
      };
    }
  }, [updateSuccess, returnUrl, history]);

  useEffect(() => {
    if (errorUpdating && errorUpdating.errorCode === 402) {
      const { rawException } = errorUpdating;

      if (
        "threeDSecureActionTokenId" in (rawException as any) &&
        (rawException as any).threeDSecureActionTokenId !== null
      ) {
        handleThreeDsToken(
          (rawException as any).threeDSecureActionTokenId as string,
          (rawException as any).updateBillingInfo,
        );
      }
    }

    return () => {};
  }, [handleThreeDsToken, errorUpdating]);

  const errorMessage = useMemo(() => {
    if (errorWithCanUpdateBillingInfo) {
      return errorWithCanUpdateBillingInfo.message;
    }

    if (errorLoading) {
      return errorLoading.message;
    }

    if (errorUpdating) {
      return errorUpdating.message;
    }

    if (error) {
      return error;
    }

    return undefined;
  }, [errorLoading, errorUpdating, error, errorWithCanUpdateBillingInfo]);

  const successMessage = useMemo(() => {
    if (updateSuccess) {
      return updateSuccessMessage;
    }

    return undefined;
  }, [updateSuccess]);

  if (!loadingCanUpdateBillingInfo && !errorWithCanUpdateBillingInfo && !canUpdateBillingInfo) {
    return <Redirect to={`/business/${businessId}/billing-details`} />;
  }

  return (
    <PublicLayout
      title="Billing Information"
      header={
        <p className={styles.header}>
          Update billing information and payment method for your Lens Subscriptions.
          <br />
          All changes made here will take effect with the next invoice.
        </p>
      }
      size="xl"
      wrapperClassName={styles.updateBusinessBillingDetails}
      notification={successMessage || errorMessage}
      notificationProps={successMessage ? { level: "success", type: "flash" } : { level: "error", type: "flash" }}
      footer={
        <>
          <LinkButton to={returnUrl} label="Cancel" discreet textTransform="uppercase" />
          <Button
            label="Update Billing Details"
            primary
            textTransform="uppercase"
            loading={busy}
            onClick={() => handleUpdateBillingDetails()}
          />
        </>
      }
    >
      <form
        id="update-billing-details-form"
        aria-label="Update Billing Details"
        autoComplete="on"
        lang="en"
        onSubmit={handleSubmit}
        ref={formRef}
      >
        <BillingForm
          values={formValues}
          updateValue={updateField}
          showInvoiceMethod={showInvoiceMethod(billingInfo)}
          loading={busy}
          isAustralia={isAustralia}
        />
        {isCreditCard ? (
          <CreditCardForm
            className={styles.creditCardForm}
            loading={loading || isSubmitting}
            firstName={formValues.firstName.value}
            lastName={formValues.lastName.value}
            paymentMethod={billingInfo?.paymentMethod}
            errors={formErrors}
          />
        ) : null}
      </form>
      {busy ? <LoadingIndicator size="2xl" /> : null}
    </PublicLayout>
  );
};

const Wrapper = () => {
  return (
    <Elements>
      <UpdateBusinessBillingDetails />
    </Elements>
  );
};

export default Wrapper;
